import { RefObject, useEffect } from 'react';

/**
 * Orchestrates the initialization sequence for the video player and all integrations.
 * 
 * INITIALIZATION ORDER (critical for proper functionality):
 * --------------------------------------------------------
 * 1. Wait for external scripts (IMA SDK, System73) to load
 * 2. Initialize System73 wrapper (if configured)
 * 3. Initialize Shaka Player (skips auto-load if System73/ads are present)
 * 4. Wrap player with System73 (MUST happen before manifest loads)
 * 5. Load manifest explicitly (after System73 wrapper is ready)
 * 6. Setup event listeners & quality tracking
 * 7. Initialize Shaka UI
 * 8. Initialize Google IMA ads (if configured)
 * 
 * WHY THIS ORDER MATTERS:
 * ----------------------
 * - System73 needs to wrap the player BEFORE manifest loads to intercept
 *   manifest parsing and detect all ABR (adaptive bitrate) profiles. If it
 *   wraps after load, it only sees the single bitrate that Shaka initially
 *   selected, causing streams to report as mono-bitrate.
 * 
 * - Ads need to intercept media loading for pre-roll playback. The IMA SDK
 *   must be initialized after the player and UI are ready but before content
 *   starts playing.
 * 
 * - Event listeners and quality tracking need the player to be fully loaded
 *   with a manifest to detect available tracks and qualities.
 * 
 * RELATIONSHIP TO OTHER HOOKS:
 * ---------------------------
 * - useShakaPlayer: Provides the low-level methods (initializePlayer, loadManifest)
 *   but doesn't control timing. This hook orchestrates WHEN to call them.
 * 
 * - useSystem73/useAds: Provide initialization methods for third-party SDKs.
 *   This hook ensures they're called in the correct sequence.
 */

interface UsePlayerInitializationProps {
  videoRef: RefObject<HTMLVideoElement>;
  playerRef: RefObject<any>;
  isScriptsLoaded: boolean;
  setIsInitialLoading: (loading: boolean) => void;

  // Configs
  system73Config?: { apiKey?: string; contentSteeringEndpoint?: string; channelId?: string };
  shakaConfig?: any;
  imaConfig?: { adTagUrl?: string };

  // Initialization functions
  initializeSystem73: (playerConfig: any) => any;
  initializePlayer: (video: HTMLVideoElement) => Promise<void>;
  loadManifest: () => Promise<void>;
  setupEventListeners: () => void;
  setupQualityTracking: () => (() => void) | undefined;
  configureQuality: () => void;
  updateAvailableQualities: () => void;
  initializeUI: () => Promise<unknown>;
  initializeAds: () => Promise<void>;
  handleMuxError: (error: unknown) => void;

  // Cleanup functions
  cleanupEventListeners: () => void;
  cleanupAdEventListeners: () => void;
  destroyUI: () => void;
  destroyMux: () => void;
  destroyPlayer: () => void;

  // Dependencies for re-initialization
  src: any;
  hasPlaylist: boolean;
  managedMode: boolean;
  bfResetKey: number;
}

export const usePlayerInitialization = ({
  videoRef,
  playerRef,
  isScriptsLoaded,
  setIsInitialLoading,
  system73Config,
  shakaConfig,
  imaConfig,
  initializeSystem73,
  initializePlayer,
  loadManifest,
  setupEventListeners,
  setupQualityTracking,
  configureQuality,
  updateAvailableQualities,
  initializeUI,
  initializeAds,
  handleMuxError,
  cleanupEventListeners,
  cleanupAdEventListeners,
  destroyUI,
  destroyMux,
  destroyPlayer,
  src,
  hasPlaylist,
  managedMode,
  bfResetKey,
}: UsePlayerInitializationProps) => {
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isScriptsLoaded) return;

    // Loading state handlers
    const onLoadStart = () => setIsInitialLoading(true);
    const onCanPlay = () => setIsInitialLoading(false);
    const onPlaying = () => setIsInitialLoading(false);

    video.addEventListener('loadstart', onLoadStart);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('playing', onPlaying);

    let currentShakaConfig = shakaConfig;

    const initialize = async () => {
      try {
        setIsInitialLoading(true);

        // ============================================================
        // STEP 1: Initialize System73 Wrapper (if configured)
        // ============================================================
        // Create the wrapper first so it can modify the player config before
        // the Shaka Player instance is created
        let system73Wrapper = null;
        if (system73Config?.apiKey && window.S73ShakaPlayerWrapper) {
          const playerConfig = { ...currentShakaConfig };
          system73Wrapper = initializeSystem73(playerConfig);

          if (system73Wrapper) {
            currentShakaConfig = playerConfig;
          }
        }

        // ============================================================
        // STEP 2: Initialize Shaka Player (without loading manifest)
        // ============================================================
        // Note: initializePlayer() will skip automatic manifest loading when
        // System73 or ads are configured (hasSystem73=true or hasAds=true)
        await initializePlayer(video);

        // ============================================================
        // STEP 3: Wrap Player with System73 & Load Manifest
        // ============================================================
        // CRITICAL: wrapPlayer() MUST be called before loadManifest()
        // so System73 can intercept manifest parsing and detect all ABR profiles
        if (system73Wrapper && playerRef.current) {
          system73Wrapper.wrapPlayer(playerRef.current);

          // Load manifest now that System73 is ready (unless ads need to load first)
          if (!imaConfig?.adTagUrl) {
            await loadManifest();
          }
        }

        // ============================================================
        // STEP 4: Setup Event Handlers & Quality Tracking
        // ============================================================
        setupEventListeners();
        setupQualityTracking();
        configureQuality();

        // Give player time to detect qualities after manifest loads
        setTimeout(() => {
          updateAvailableQualities();
        }, 100);

        // ============================================================
        // STEP 5: Initialize Shaka UI
        // ============================================================
        await initializeUI();

        // ============================================================
        // STEP 6: Initialize Google IMA Ads (if configured)
        // ============================================================
        // Ads are initialized last because they need the player, UI, and
        // all event listeners to be ready. The ads loader will call
        // loadManifest() after pre-roll ads complete.
        if (imaConfig?.adTagUrl && window.google?.ima) {
          await initializeAds();
        }
      } catch (error) {
        console.error('Player initialization error:', error);
        handleMuxError(error);
      }
    };

    initialize();

    return () => {
      video.removeEventListener('loadstart', onLoadStart);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('playing', onPlaying);
      cleanupEventListeners();
      cleanupAdEventListeners();
      destroyUI();
      destroyMux();
      destroyPlayer();
    };
  // Dependency array optimization:
  // - managedMode=false: Reinitialize on every src change (direct Player usage)
  // - managedMode=true: Only reinitialize when hasPlaylist boolean changes (wrapper usage)
  // This prevents unnecessary reinitialization when wrapper components refetch data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managedMode ? hasPlaylist : src, isScriptsLoaded, bfResetKey, managedMode]);
};
